/**
 * Hook for subscribing to link change events via GraphQL WebSocket
 *
 * Replaces REST polling with real-time GraphQL subscriptions.
 * Events are user-specific (per-user channels on backend).
 */

import createDebug from 'debug';
import { useEffect, useRef } from 'react';
import { useSubscription, gql } from 'urql';
import type { ChangeAction } from '@/graphql/generated/graphql';
import type { LinkSummaryResponse, TagResponse } from '@/api/client';
import { decodeGlobalId } from '@/lib/global-id';

const debug = createDebug('app:subscription');

// Subscription document - parsed at runtime for graphcache compatibility
const LinkChangesSubscription = gql`
	subscription LinkChanges {
		linkChanges {
			action
			linkId
			previousQueueId
			timestamp
			link {
				id
				url
				title
				description
				queueId
				thumbnailUrl
				isEnriching
				updatedAt
				tags(first: 50) {
					edges {
						node {
							id
							name
							color
							icon
							isNsfw
							isCategory
							isPersonal
						}
					}
				}
			}
		}
	}
`;

export interface LinkChangeEvent {
	action: ChangeAction;
	linkId: number;
	link?: LinkSummaryResponse;
	previousQueueId?: number;
	queueId: number;
	timestamp: string;
}

export interface UseLinkSubscriptionOptions {
	onLinkChange: (event: LinkChangeEvent) => void;
	enabled?: boolean;
}

export interface UseLinkSubscriptionResult {
	connected: boolean;
	error: Error | null;
}

// Runtime type for the link data (fragments are flattened at runtime)
interface LinkData {
	id: string;
	url: string;
	title: string;
	description?: string | null;
	queueId: number;
	thumbnailUrl?: string | null;
	isEnriching: boolean;
	updatedAt: string;
	tags: {
		edges: Array<{
			node: {
				id: string;
				name: string;
				color?: string | null;
				icon?: string | null;
				isNsfw: boolean;
				isCategory: boolean;
				isPersonal?: boolean | null;
			};
		}>;
	};
}

/**
 * Convert GraphQL link data to REST API response format
 * Note: GraphQL IDs are base64-encoded global IDs (e.g., "TGluazoxMjM=" for "Link:123")
 */
function toLinkSummary(link: LinkData): LinkSummaryResponse {
	const tags: TagResponse[] = link.tags.edges.map((edge) => ({
		id: decodeGlobalId(edge.node.id),
		name: edge.node.name,
		color: edge.node.color ?? null,
		icon: edge.node.icon ?? null,
		isNsfw: edge.node.isNsfw,
		isCategory: edge.node.isCategory,
		isPersonal: edge.node.isPersonal ?? false,
	}));

	return {
		id: decodeGlobalId(link.id),
		url: link.url,
		title: link.title,
		description: link.description ?? null,
		queueId: link.queueId,
		thumbnailUrl: link.thumbnailUrl ?? null,
		isEnriching: link.isEnriching,
		updatedAt: link.updatedAt,
		tags,
	};
}

export function useLinkSubscription(options: UseLinkSubscriptionOptions): UseLinkSubscriptionResult {
	const { onLinkChange, enabled = true } = options;

	// Track previous event to deduplicate
	const prevEventKeyRef = useRef<string | null>(null);
	const prevStateRef = useRef<{ fetching: boolean; error: boolean } | null>(null);

	const [result] = useSubscription({
		query: LinkChangesSubscription,
		pause: !enabled,
	});

	// Log connection state changes
	useEffect(() => {
		const currentState = { fetching: result.fetching, error: !!result.error };
		const prevState = prevStateRef.current;

		if (!prevState) {
			debug('subscription init: enabled=%s fetching=%s', enabled, result.fetching);
		} else if (prevState.fetching !== currentState.fetching || prevState.error !== currentState.error) {
			if (result.error) {
				debug('subscription error: %s', result.error.message);
			} else if (!result.fetching && prevState.fetching) {
				debug('subscription connected');
			} else if (result.fetching && !prevState.fetching) {
				debug('subscription connecting...');
			}
		}

		prevStateRef.current = currentState;
	}, [enabled, result.fetching, result.error]);

	// Process subscription events
	useEffect(() => {
		if (!result.data?.linkChanges) return;

		const event = result.data.linkChanges;

		// Deduplicate - urql may fire multiple times for same event
		const eventKey = `${event.action}:${event.linkId}:${event.timestamp}`;
		if (prevEventKeyRef.current === eventKey) {
			debug('subscription duplicate event (skipped): %s', eventKey);
			return;
		}
		prevEventKeyRef.current = eventKey;

		// Decode global ID: base64("Link:123") -> "Link:123" -> 123
		const decoded = atob(event.linkId);
		const linkId = parseInt(decoded.split(':')[1], 10);

		debug(
			'subscription event: action=%s linkId=%d queueId=%s prevQueueId=%s',
			event.action,
			linkId,
			event.link?.queueId ?? 'n/a',
			event.previousQueueId ?? 'n/a'
		);

		// Transform link data if present (cast to runtime type - fragments are flattened)
		let linkSummary: LinkSummaryResponse | undefined;
		if (event.link) {
			linkSummary = toLinkSummary(event.link as unknown as LinkData);
		}

		// Determine queueId from link or default to 0
		const queueId = linkSummary?.queueId ?? 0;

		onLinkChange({
			action: event.action,
			linkId,
			link: linkSummary,
			previousQueueId: event.previousQueueId ?? undefined,
			queueId,
			timestamp: event.timestamp,
		});
	}, [result.data, onLinkChange]);

	return {
		connected: !result.fetching && !result.error,
		error: result.error ?? null,
	};
}
